import { useRef, useState } from "react";
import { UploadCloud, Download, CheckCircle2, Plus, Trash2, Loader2, History, Mail } from "lucide-react";
import { VersioniDocumento } from "@/components/pratica/VersioniDocumento";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { STATO_DOC, RUOLI } from "@/lib/constants";
import { RoleBadge, StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function DocRow({ doc, onChange }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [vers, setVers] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/documenti/${doc.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data); toast.success(`Caricato: ${file.name}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    try { const { data } = await api.patch(`/documenti/${doc.id}`, { stato: doc.stato === "verificato" ? "caricato" : "verificato" }); onChange({ ...doc, ...data }); } catch (err) { toast.error(formatApiError(err)); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/files/${doc.file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = doc.file.original_filename; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/documenti/${doc.id}`); onChange(null); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`doc-row-${doc.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium text-slate-900">{doc.nome}{doc.obbligatorio && <span className="text-red-500 ml-1">*</span>}</div>
        <div className="flex items-center gap-2 mt-1"><RoleBadge role={doc.ruolo} />{doc.file && <span className="text-xs text-slate-500 truncate max-w-[220px]">{doc.file.original_filename}</span>}{doc.caricato_dal_cliente && <span className="text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5" data-testid={`doc-cliente-badge-${doc.id}`}>caricato dal cliente</span>}</div>
      </div>
      <StatusBadge map={STATO_DOC} value={doc.stato} testId={`doc-status-${doc.id}`} />
      <div className="flex items-center gap-1">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`doc-file-input-${doc.id}`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current.click()} data-testid={`doc-upload-${doc.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}<span className="ml-1.5 hidden sm:inline">{doc.file ? "Sostituisci" : "Carica"}</span></Button>
        {doc.file && <Button size="sm" variant="ghost" onClick={download} data-testid={`doc-download-${doc.id}`} title="Scarica"><Download size={14} /></Button>}
        {doc.versioni > 0 && <Button size="sm" variant="ghost" onClick={() => setVers(true)} data-testid={`doc-versioni-${doc.id}`} title="Versioni" className="text-slate-500"><History size={14} /><span className="ml-1 text-xs">{doc.versioni}</span></Button>}
        {vers && <VersioniDocumento doc={doc} open={vers} onOpenChange={setVers} onRipristinato={async () => { const { data } = await api.get(`/pratiche/${doc.pratica_id}/documenti`); const nd = data.find((x) => x.id === doc.id); if (nd) onChange(nd); }} />}
        {doc.file && <Button size="sm" variant="ghost" onClick={verify} data-testid={`doc-verify-${doc.id}`} title="Verifica" className={doc.stato === "verificato" ? "text-emerald-600" : "text-slate-500"}><CheckCircle2 size={14} /></Button>}
        {!doc.obbligatorio && <Button size="sm" variant="ghost" onClick={remove} data-testid={`doc-delete-${doc.id}`} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></Button>}
      </div>
    </div>
  );
}

export function DocumentChecklist({ pratica, docs, setDocs }) {
  const [adding, setAdding] = useState(null);
  const [nuovo, setNuovo] = useState({ nome: "", ruolo: "legale" });

  const update = (d, idx) => setDocs(d ? docs.map((x) => (x.id === d.id ? { ...x, ...d } : x)) : docs.filter((_, i) => i !== idx));
  const add = async (fase) => {
    if (!nuovo.nome.trim()) return;
    try {
      const { data } = await api.post(`/pratiche/${pratica.id}/documenti`, { ...nuovo, fase, obbligatorio: false });
      setDocs([...docs, data]); setNuovo({ nome: "", ruolo: "legale" }); setAdding(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [sollecito, setSollecito] = useState(false);
  const mancanti = docs.filter((d) => d.obbligatorio && d.stato === "mancante").length;
  const sollecita = async () => { setSollecito(true); try { const { data } = await api.post(`/pratiche/${pratica.id}/documenti/sollecito`); toast.success(`Promemoria inviato a ${data.to} (${data.documenti} documenti)`); } catch (err) { toast.error(formatApiError(err)); } finally { setSollecito(false); } };

  return (
    <div className="space-y-6" data-testid="document-checklist">
      {mancanti > 0 && <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="documenti-mancanti-box"><span><b>{mancanti}</b> documenti obbligatori mancanti. Il cliente riceve un promemoria automatico ogni settimana dal cron (documenti attesi da oltre 7 giorni), con link per caricarli dall'Area Cliente.</span><Button size="sm" variant="outline" onClick={sollecita} disabled={sollecito} className="ml-auto h-8 text-xs bg-white" data-testid="documenti-sollecita">{sollecito ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} className="mr-1" />} Sollecita ora il cliente</Button></div>}
      {pratica.fasi.map((f) => {
        const list = docs.filter((d) => d.fase === f.key);
        return (
          <div key={f.key} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-medium text-slate-900">{f.nome}</h3>
              <span className="text-xs text-slate-500">{list.filter((d) => d.stato !== "mancante").length}/{list.length} presenti</span>
            </div>
            {list.map((d) => <DocRow key={d.id} doc={d} onChange={(nd) => update(nd, docs.indexOf(d))} />)}
            {adding === f.key ? (
              <div className="flex flex-wrap gap-2 mt-3">
                <Input autoFocus placeholder="Nome documento" value={nuovo.nome} onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} className="flex-1 min-w-[200px] bg-slate-50" data-testid="new-doc-name-input" />
                <select value={nuovo.ruolo} onChange={(e) => setNuovo({ ...nuovo, ruolo: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-slate-50 px-2 text-sm" data-testid="new-doc-role-select">
                  {Object.entries(RUOLI).filter(([k]) => k !== "admin").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <Button size="sm" onClick={() => add(f.key)} className="bg-[#003087]" data-testid="new-doc-save-button">Aggiungi</Button>
                <Button size="sm" variant="ghost" onClick={() => setAdding(null)}>Annulla</Button>
              </div>
            ) : (
              <button onClick={() => setAdding(f.key)} className="mt-3 text-xs font-medium text-[#003087] hover:underline inline-flex items-center gap-1" data-testid={`add-doc-${f.key}`}><Plus size={13} /> Aggiungi documento</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
